import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * V1 DRAFT - authored for structural completeness; NOT a substitute for sign-off
 * by a Czech osoba odborně způsobilá v prevenci rizik. Legal refs per the Czech
 * BOZP research (docs/specs/2026-06-23-bozp-research-czech-legal.md).
 *
 * Nebezpečné chemické látky - Czech-only osnova (LocalizedText cs-CZ). 3 chapters
 * (bezpečnostní list / OOPP / únik) + a graded quiz with one question per kind.
 * Answer keys live here (repo-privileged) and in the DB; stripped from the client
 * payload by the API.
 */
export const hazardousChemicalsProfile: TrainingProfile = {
  slug: 'hazardous-chemicals',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Nebezpečné chemické látky',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-sds',
        title: t('Bezpečnostní list a značení', 'Safety data sheet and labelling'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-sds'],
        blocks: [
          {
            id: 'h-sds',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Bezpečnostní list (SDS)', 'Safety data sheet (SDS)'),
          },
          {
            id: 'p-sds-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Před prací s každou nebezpečnou chemickou látkou nebo směsí si vyžádej a prostuduj **bezpečnostní list (SDS)**. Bezpečnostní list je strukturován do **16 oddílů** a popisuje rizika, první pomoc, podmínky skladování i doporučené osobní ochranné prostředky.',
              'Before working with any hazardous chemical or mixture, request and study the **safety data sheet (SDS)**. The safety data sheet is structured into **16 sections** and describes the hazards, first aid, storage conditions, and the recommended personal protective equipment.',
            ),
          },
          {
            id: 'c-sds-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **nařízení CLP (ES) 1272/2008** o klasifikaci, označování a balení; hygienické limity expozice stanoví **NV 361/2007 Sb.**',
              'Legal basis: the **CLP Regulation (EC) 1272/2008** on classification, labelling and packaging; occupational exposure limits are set by **Gov. Reg. 361/2007 Coll.**',
            ),
          },
          {
            id: 'anim-lahve',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'chemicalBottles',
            steps: [
              {
                label: t('Označená nádoba', 'Labelled container'),
                say: t(
                  'Originální obal s etiketou a bezpečnostním listem.',
                  'The original container with label and safety data sheet.',
                ),
              },
              {
                label: t('Láhev od pití', 'Drink bottle'),
                say: t(
                  'Nikdy nepřelévej - riziko záměny a otravy.',
                  'Never decant - risk of mix-up and poisoning.',
                ),
              },
            ],
            caption: t(
              'Chemikálie nikdy nepřelévej do lahví od nápojů - hrozí záměna a otrava.',
              'Never decant chemicals into drink bottles - there is a risk of a mix-up and poisoning.',
            ),
          },
          {
            id: 'p-sds-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Na obalu rozpoznáš nebezpečí podle **výstražných symbolů (GHS piktogramy)**, signálního slova **Nebezpečí** nebo **Varování**, a podle **H-vět** (standardní věty o nebezpečnosti) a **P-vět** (pokyny pro bezpečné zacházení).',
              'On the packaging you recognise the hazard from the **hazard symbols (GHS pictograms)**, the signal word **Danger** or **Warning**, and from the **H-statements** (standardised hazard statements) and **P-statements** (precautionary statements for safe handling).',
            ),
          },
          {
            id: 'acc-sds',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t('Co znamená H-věta?', 'What does an H-statement mean?'),
                a: t(
                  'H-věta (hazard statement) popisuje druh nebezpečnosti látky, např. H225 „Vysoce hořlavá kapalina a páry“.',
                  'An H-statement (hazard statement) describes the type of hazard of a substance, e.g. H225 “Highly flammable liquid and vapour”.',
                ),
              },
              {
                q: t('Kde najdu doporučené OOPP?', 'Where do I find the recommended PPE?'),
                a: t(
                  'Doporučené osobní ochranné prostředky jsou uvedeny v oddílu 8 bezpečnostního listu (omezování expozice / ochrana osob).',
                  'The recommended personal protective equipment is given in section 8 of the safety data sheet (exposure controls / personal protection).',
                ),
              },
            ],
          },
          {
            id: 'kc-sds',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Co popisují H-věty na obalu chemické látky?',
              'What do the H-statements on a chemical’s packaging describe?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t('Druh nebezpečnosti látky', 'The type of hazard of the substance'),
              },
              {
                id: 'kc-b',
                label: t('Cenu a hmotnost balení', 'The price and weight of the package'),
              },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'H-věty (hazard statements) podle CLP popisují povahu nebezpečnosti, P-věty pak pokyny pro bezpečné zacházení.',
              'Under CLP, H-statements (hazard statements) describe the nature of the hazard, while P-statements give the precautions for safe handling.',
            ),
          },
          {
            id: 'k-sds',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Nikdy nepracuj s neoznačenou nebo neznáme látkou. **Bezpečnostní list čti dřív, než otevřeš obal.**',
              'Never work with an unlabelled or unknown substance. **Read the safety data sheet before you open the container.**',
            ),
          },
        ],
      },
      {
        id: 'ch-oopp',
        title: t('Osobní ochranné prostředky (OOPP)', 'Personal protective equipment (PPE)'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-oopp',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Ochrana zdraví při práci s chemií',
              'Protecting your health when working with chemicals',
            ),
          },
          {
            id: 'p-oopp-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'OOPP se volí podle oddílu 8 bezpečnostního listu a podle skutečné expozice. Zaměstnavatel je poskytuje **bezplatně**. OOPP jsou **až posledním článkem** ochrany - přednost mají technická a organizační opatření (větrání, náhrada méně nebezpečnou látkou).',
              'PPE is chosen according to section 8 of the safety data sheet and the actual exposure. The employer provides it **free of charge**. PPE is the **last line** of protection - technical and organisational measures (ventilation, substitution with a less hazardous substance) take precedence.',
            ),
          },
          {
            id: 'cl-oopp',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Chemicky odolné rukavice odpovídající dané látce',
                'Chemically resistant gloves suited to the given substance',
              ),
              t('Ochranné brýle nebo obličejový štít', 'Safety goggles or a face shield'),
              t(
                'Respirátor s vhodným filtrem při riziku par či aerosolů',
                'A respirator with a suitable filter where there is a risk of vapours or aerosols',
              ),
              t('Ochranný oděv a uzavřená obuv', 'Protective clothing and closed footwear'),
              t(
                'Kontrola neporušenosti OOPP před každým použitím',
                'Checking the integrity of the PPE before each use',
              ),
            ],
          },
          {
            id: 'c-oopp-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Materiál rukavic musí odpovídat konkrétní látce - žádný materiál nechrání proti všem chemikáliím. Nesprávně zvolené rukavice mohou propustit látku až ke kůži.',
              'The glove material must match the specific substance - no material protects against all chemicals. Incorrectly chosen gloves can let the substance through to the skin.',
            ),
          },
          {
            id: 'c-oopp-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Nikdy nejez, nepij ani nekuř** v prostoru, kde se manipuluje s chemickými látkami. Po práci si vždy umyj ruce před jídlem.',
              '**Never eat, drink, or smoke** in an area where chemicals are being handled. After work, always wash your hands before eating.',
            ),
          },
          {
            id: 'k-oopp',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'OOPP jsou poslední, ne první ochrana. **Vždy upřednostni větrání a náhradu méně nebezpečnou látkou.**',
              'PPE is the last line of protection, not the first. **Always prefer ventilation and substitution with a less hazardous substance.**',
            ),
          },
        ],
      },
      {
        id: 'ch-unik',
        title: t('Únik a havárie', 'Spills and emergencies'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-unik'],
        blocks: [
          {
            id: 'h-unik',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Postup při úniku chemické látky', 'Procedure for a chemical spill'),
          },
          {
            id: 'p-unik-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Při úniku nebezpečné látky je prioritou **zdraví osob**, až poté majetek a životní prostředí. Nejdřív zajisti vlastní bezpečnost a varuj okolí, teprve pak zvaž zastavení úniku, je-li to bezpečné.',
              'In a spill of a hazardous substance the priority is **people’s health**, then property and the environment. First secure your own safety and warn those around you; only then consider stopping the spill, if it is safe to do so.',
            ),
          },
          {
            id: 'ord-unik',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky při úniku chemické látky do správného pořadí.',
              'Put the steps for a chemical spill into the correct order.',
            ),
            items: [
              {
                id: 'ou-1',
                label: t(
                  'Varuj okolí a opusť zasažený prostor',
                  'Warn those around you and leave the affected area',
                ),
              },
              { id: 'ou-2', label: t('Nasaď vhodné OOPP', 'Put on suitable PPE') },
              {
                id: 'ou-3',
                label: t(
                  'Zastav nebo ohranič únik, je-li to bezpečné',
                  'Stop or contain the spill, if it is safe',
                ),
              },
              {
                id: 'ou-4',
                label: t(
                  'Zachyť látku sorbentem a zlikviduj jako nebezpečný odpad',
                  'Absorb the substance with a sorbent and dispose of it as hazardous waste',
                ),
              },
            ],
          },
          {
            id: 'c-unik-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Při velkém úniku, požáru nebo zasažení osob volej okamžitě 150 (hasiči) nebo 155 (ZZS).** Nikdy se nepokoušej likvidovat únik nad rámec svých možností.',
              '**In a large spill, a fire, or when people are exposed, call 150 (fire brigade) or 155 (emergency medical service) immediately.** Never attempt to deal with a spill beyond your capabilities.',
            ),
          },
          {
            id: 'acc-unik',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t(
                  'Co dělat při zasažení kůže nebo očí?',
                  'What to do if the skin or eyes are exposed?',
                ),
                a: t(
                  'Okamžitě oplachovat velkým množstvím vody po dobu uvedenou v bezpečnostním listu a řídit se oddílem 4 (první pomoc); při zasažení očí vyhledat lékaře.',
                  'Rinse immediately with plenty of water for the time stated in the safety data sheet and follow section 4 (first aid); if the eyes are affected, seek medical attention.',
                ),
              },
              {
                q: t('Jak naložit s použitým sorbentem?', 'How to handle a used sorbent?'),
                a: t(
                  'Použitý sorbent i zbytky látky jsou nebezpečný odpad - uložit do označené nádoby a předat oprávněné osobě k likvidaci.',
                  'The used sorbent and any residues of the substance are hazardous waste - place them in a labelled container and hand them over to an authorised person for disposal.',
                ),
              },
            ],
          },
          {
            id: 'k-unik',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Pořadí priorit: **lidé → majetek → životní prostředí.** Tísňové linky: hasiči **150**, ZZS **155**.',
              'Order of priorities: **people → property → the environment.** Emergency lines: fire brigade **150**, emergency medical service **155**.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-sds-oddily',
      kind: 'numeric',
      chapterId: 'ch-sds',
      prompt: t(
        'Z kolika oddílů se skládá bezpečnostní list (SDS)?',
        'How many sections does a safety data sheet (SDS) consist of?',
      ),
      correct: 16,
      tolerance: 0,
    },
    {
      id: 'q-hveta',
      kind: 'single',
      chapterId: 'ch-sds',
      prompt: t(
        'Co popisuje H-věta na obalu chemické látky?',
        'What does an H-statement on a chemical’s packaging describe?',
      ),
      choices: [
        { id: 'a', label: t('Pokyny pro bezpečné zacházení', 'Precautions for safe handling') },
        { id: 'b', label: t('Druh nebezpečnosti látky', 'The type of hazard of the substance') },
        { id: 'c', label: t('Datum spotřeby', 'The expiry date') },
      ],
      correct: 'b',
    },
    {
      id: 'q-oopp-tf',
      kind: 'truefalse',
      chapterId: 'ch-oopp',
      prompt: t(
        'Jeden druh ochranných rukavic chrání proti všem chemickým látkám.',
        'One type of protective glove protects against all chemicals.',
      ),
      correct: false,
    },
    {
      id: 'q-oopp-multi',
      kind: 'multi',
      chapterId: 'ch-oopp',
      prompt: t(
        'Které prostředky patří mezi OOPP pro práci s chemií? (vyber všechny správné)',
        'Which items are PPE for working with chemicals? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Chemicky odolné rukavice', 'Chemically resistant gloves') },
        { id: 'b', label: t('Ochranné brýle', 'Safety goggles') },
        {
          id: 'c',
          label: t('Respirátor s vhodným filtrem', 'A respirator with a suitable filter'),
        },
        { id: 'd', label: t('Reflexní vesta', 'Hi-vis vest') },
      ],
      correct: ['a', 'b', 'c'],
    },
    {
      id: 'q-unik-order',
      kind: 'ordering',
      chapterId: 'ch-unik',
      prompt: t('Seřaď kroky při úniku chemické látky.', 'Order the steps for a chemical spill.'),
      choices: [
        { id: 'a', label: t('Varuj okolí', 'Warn those around you') },
        { id: 'b', label: t('Nasaď OOPP', 'Put on PPE') },
        { id: 'c', label: t('Zastav nebo ohranič únik', 'Stop or contain the spill') },
        { id: 'd', label: t('Zachyť sorbentem a zlikviduj', 'Absorb with a sorbent and dispose') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
